// hooks/useCalendarDataCached.js - VERSION CORRIGÉE avec données centralisées
import { useMemo } from 'react';

export const useCalendarDataCached = (data, hooks) => {
    // NOUVEAU : Utiliser les données centralisées au lieu des hooks individuels
    const {
        objectifs = [],
        plans = [],
        isLoading = false,
        hasError = false,
        errors = {},
        isFromCache = false,
        cacheInfo = {}
    } = data || {};

    const {
        refreshObjectifs,
        refreshPlans,
        refreshAll,
        plansCachedHook
    } = hooks || {};

    // Récupérer updateTaskStatus depuis les hooks
    const updateTaskStatus = plansCachedHook?.updateTaskStatus || null;

    // Fonction de rafraîchissement centralisée optimisée
    const handleRefresh = () => {
        console.log('🔄 Rafraîchissement Calendar avec données centralisées');
        
        // Utiliser le rafraîchissement global centralisé si disponible
        if (refreshAll && typeof refreshAll === 'function') {
            refreshAll();
        } else {
            // Fallback sur les rafraîchissements individuels
            if (refreshObjectifs && typeof refreshObjectifs === 'function') {
                refreshObjectifs();
            }
            if (refreshPlans && typeof refreshPlans === 'function') {
                refreshPlans();
            }
        }
    };

    // OPTIMISÉ : Créer un mapping des objectifs pour accès rapide (une seule fois)
    const objectifsMap = useMemo(() => {
        const map = new Map();
        objectifs.forEach(objectif => {
            if (objectif.id_objectif) {
                map.set(objectif.id_objectif, objectif);
            }
            if (objectif.id) {
                map.set(objectif.id, objectif);
            }
        });
        return map;
    }, [objectifs]);

    // Fonction pour mapper les statuts d'affichage (inchangée)
    const mapStatusDisplay = (status) => {
        const statusMapping = {
            'a_faire': 'À faire',
            'en_cours': 'En cours',
            'fait': 'Fait',
            'annule': 'Annulé',
            'pending': 'À faire',
            'in-progress': 'En cours',
            'completed': 'Fait',
            'cancelled': 'Annulé'
        };
        return statusMapping[status] || status;
    };

    // Fonction pour obtenir les couleurs par priorité (inchangée)
    const getPriorityColor = (priority) => {
        const colors = {
            'high': { bg: '#fef2f2', border: '#ef4444', text: '#991b1b' },
            'medium': { bg: '#fef3c7', border: '#f59e0b', text: '#92400e' },
            'low': { bg: '#f0f9ff', border: '#3b82f6', text: '#1e40af' }
        };
        return colors[priority] || colors.medium;
    };

    // Fonction pour obtenir les couleurs par statut (inchangée)
    const getStatusColor = (status) => {
        const colors = {
            'Fait': { bg: '#dcfce7', border: '#22c55e', text: '#166534' },
            'En cours': { bg: '#fef3c7', border: '#f59e0b', text: '#92400e' },
            'À faire': { bg: '#f3f4f6', border: '#9ca3af', text: '#6b7280' },
            'Annulé': { bg: '#fee2e2', border: '#ef4444', text: '#991b1b' }
        };
        return colors[status] || colors['À faire'];
    };

    // OPTIMISÉ : Transformer les données en format calendrier avec cache intelligent
    const calendarData = useMemo(() => {
        const events = [];

        // Fonction pour obtenir les informations de l'objectif par ID
        const getObjectiveInfo = (objectifId) => {
            const objectif = objectifsMap.get(objectifId);
            if (objectif) {
                return {
                    name: objectif.title || 'Objectif',
                    category: objectif.category || null,
                    targetDate: objectif.target_date || null
                };
            }
            return { name: 'Objectif', category: null, targetDate: null };
        };

        // Ajouter les objectifs avec dates cibles
        objectifs.forEach(objectif => {
            if (objectif.target_date) {
                const statusMapped = mapStatusDisplay(objectif.status || 'a_faire');
                const statusColor = getStatusColor(statusMapped);

                events.push({
                    id: `objectif-${objectif.id_objectif}`,
                    title: objectif.title,
                    start: new Date(objectif.target_date),
                    end: new Date(objectif.target_date),
                    allDay: true,
                    type: 'objective',
                    status: statusMapped,
                    priority: objectif.priority || 'medium',
                    category: objectif.category || 'Autre',
                    description: objectif.description || '',
                    progress: `${objectif.current_value || 0}/${objectif.target_value || 0} ${objectif.value_unit || ''}`,
                    color: statusColor.border,
                    backgroundColor: statusColor.bg,
                    textColor: statusColor.text,
                    data: objectif,
                    // NOUVEAU : Marquer si les données viennent du cache
                    isFromCache: isFromCache
                });
            }
        });

        // Ajouter les tâches avec deadlines
        plans.forEach(plan => {
            const objectiveInfo = getObjectiveInfo(plan.id_objectif);
            
            if (plan.tasks) {
                let tasks = [];
                if (typeof plan.tasks === 'string') {
                    try {
                        tasks = JSON.parse(plan.tasks);
                    } catch (e) {
                        console.warn('Erreur parsing tasks JSON pour calendar:', e);
                        tasks = [];
                    }
                } else if (Array.isArray(plan.tasks)) {
                    tasks = plan.tasks;
                }

                tasks.forEach((task, taskIndex) => {
                    if (task.deadline) {
                        const statusMapped = mapStatusDisplay(task.status || 'a_faire');
                        const statusColor = getStatusColor(statusMapped);
                        const priorityColor = getPriorityColor(task.priority || 'medium');

                        events.push({
                            id: `task-${plan.id_plan}-${taskIndex}`,
                            title: task.title,
                            start: new Date(task.deadline),
                            end: new Date(task.deadline),
                            allDay: true,
                            type: 'task',
                            status: statusMapped,
                            priority: task.priority || 'medium',
                            category: objectiveInfo.category || 'Autre',
                            objectiveName: objectiveInfo.name,
                            planTitle: plan.title,
                            description: task.description || '',
                            estimatedHours: task.estimated_hours || null,
                            color: priorityColor.border,
                            backgroundColor: priorityColor.bg,
                            textColor: priorityColor.text,
                            data: {
                                ...task,
                                planId: plan.id_plan,
                                taskIndex: taskIndex,
                                objectifId: plan.id_objectif
                            },
                            // NOUVEAU : Marquer si les données viennent du cache
                            isFromCache: isFromCache
                        });

                        // Ajouter les sous-tâches avec deadlines
                        if (task.subtasks && Array.isArray(task.subtasks)) {
                            task.subtasks.forEach((subtask, subtaskIndex) => {
                                if (subtask.deadline) {
                                    const subStatusMapped = mapStatusDisplay(subtask.status || 'a_faire');
                                    const subStatusColor = getStatusColor(subStatusMapped);

                                    events.push({
                                        id: `subtask-${plan.id_plan}-${taskIndex}-${subtaskIndex}`,
                                        title: `↳ ${subtask.title}`,
                                        start: new Date(subtask.deadline),
                                        end: new Date(subtask.deadline),
                                        allDay: true,
                                        type: 'subtask',
                                        status: subStatusMapped,
                                        priority: subtask.priority || 'medium',
                                        category: objectiveInfo.category || 'Autre',
                                        objectiveName: objectiveInfo.name,
                                        planTitle: plan.title,
                                        parentTaskTitle: task.title,
                                        description: subtask.description || '',
                                        estimatedHours: subtask.estimated_hours || null,
                                        color: subStatusColor.border,
                                        backgroundColor: subStatusColor.bg,
                                        textColor: subStatusColor.text,
                                        data: {
                                            ...subtask,
                                            planId: plan.id_plan,
                                            taskIndex: taskIndex,
                                            subtaskIndex: subtaskIndex,
                                            isSubtask: true,
                                            parentTaskTitle: task.title
                                        },
                                        // NOUVEAU : Marquer si les données viennent du cache
                                        isFromCache: isFromCache
                                    });
                                }
                            });
                        }
                    }
                });
            }
        });

        // Trier les événements par date
        events.sort((a, b) => new Date(a.start) - new Date(b.start));

        return events;
    }, [objectifs, plans, objectifsMap, isFromCache]);

    // Extraction des données pour les filtres (inchangées)
    const availableYears = useMemo(() => {
        const years = calendarData
            .map(event => new Date(event.start).getFullYear().toString())
            .filter(Boolean);
        return [...new Set(years)].sort();
    }, [calendarData]);

    const availableMonths = useMemo(() => {
        const months = calendarData
            .map(event => {
                const date = new Date(event.start);
                return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
            })
            .filter(Boolean);
        return [...new Set(months)].sort();
    }, [calendarData]);

    const availableCategories = useMemo(() => {
        const categories = calendarData
            .map(event => event.category)
            .filter(Boolean);
        return [...new Set(categories)].sort();
    }, [calendarData]);

    const availableTypes = useMemo(() => {
        const types = calendarData
            .map(event => event.type)
            .filter(Boolean);
        return [...new Set(types)].sort();
    }, [calendarData]);

    const availableStatuses = useMemo(() => {
        const statuses = calendarData
            .map(event => event.status)
            .filter(Boolean);
        return [...new Set(statuses)].sort();
    }, [calendarData]);

    const availablePriorities = useMemo(() => {
        const priorities = calendarData
            .map(event => event.priority)
            .filter(Boolean);
        const allPossiblePriorities = ['low', 'medium', 'high'];
        const uniquePriorities = [...new Set([...priorities, ...allPossiblePriorities])];
        return uniquePriorities.sort((a, b) => {
            const order = { low: 0, medium: 1, high: 2 };
            return order[a] - order[b];
        });
    }, [calendarData]);

    return {
        // Données principales
        calendarData,
        
        // Options de filtres
        availableYears,
        availableMonths,
        availableCategories,
        availableTypes,
        availableStatuses,
        availablePriorities,
        
        // États centralisés
        isLoading,
        hasError,
        error: errors.objectifs || errors.plans,
        
        // Actions
        updateTaskStatus,
        handleRefresh,
        
        // Utilitaires
        mapStatusDisplay,
        getPriorityColor,
        getStatusColor,
        
        // NOUVEAU : Informations sur le cache
        isFromCache,
        objectifsFromCache: cacheInfo.objectifs || false,
        plansFromCache: cacheInfo.plans || false
    };
};